/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Redirect } from 'react-router-dom';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { enqueueSnackbar } from 'notistack';
import { saveAs } from 'file-saver';
import { GlobalContext } from 'providers/GlobalProvider';
import { MetricsAPI } from '../../api/MetricsApi';
import { GroupAPI } from '../../api/GroupAPI';
import { LicensesAPI } from '../../api/LicensesAPI';
import { CreditsAPI } from '../../api/CreditsAPI';
import '../../resources/styles/leadership.css';

// ---------------------------------------------------------------------------
// Control styling — mirrors the product Country-of-Origin dropdown on the
// import screening page (Search.tsx: productCountryAutocompleteSx) so the
// leadership selectors match: 44px themed input, 8px radius, no notched
// outline, Inter 14px, and a themed option list.
// ---------------------------------------------------------------------------
const controlInputSx = {
  '& .MuiOutlinedInput-root': {
    height: '44px',
    borderRadius: '8px',
    background: 'var(--Background-200, var(--bg-colors))',
    border: '1px solid var(--es-soft-border)',
    color: 'var(--text-primary-color)',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  '& .MuiAutocomplete-inputRoot': {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
  },
  '& .MuiInputBase-input': {
    color: 'var(--text-primary-color)',
    fontFamily: 'Inter',
    fontSize: '14px',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--text-secondary)',
  },
} as const;

// MUI Select renders its own OutlinedInput; match the same themed shell.
const controlSelectSx = {
  height: '44px',
  borderRadius: '8px',
  background: 'var(--Background-200, var(--bg-colors))',
  border: '1px solid var(--es-soft-border)',
  color: 'var(--text-primary-color)',
  fontFamily: 'Inter',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--text-secondary)',
  },
} as const;

// Themed option list, shared by both selectors.
const controlListComponentsProps = {
  popper: {
    sx: {
      '& .MuiAutocomplete-option': {
        color: 'var(--text-primary-white) !important',
      },
    },
  },
  paper: {
    sx: {
      color: 'var(--text-primary-white) !important',
    },
  },
} as const;

const controlMenuProps = {
  PaperProps: {
    sx: {
      color: 'var(--text-primary-white) !important',
    },
  },
} as const;

// ---------------------------------------------------------------------------
// Team option: what the selector lists. `id` feeds the group_id-scoped metric
// calls (databases/analyzers/saved-work) + licenses/credits; `name` feeds
// getUniqueUsers, which matches KeycloakEventLogs.teams by team name — NOT id.
// ---------------------------------------------------------------------------
interface TeamOption {
  id: string;
  name: string;
}
const ALL_TEAMS: TeamOption = { id: '__all__', name: 'All teams' };

const TIER_META: { key: string; label: string; color: string }[] = [
  { key: 'enterprise', label: 'Enterprise', color: 'var(--accent-color)' },
  { key: 'business', label: 'Business', color: 'var(--action-color)' },
  { key: 'pro', label: 'Pro', color: 'var(--full-bar-color)' },
  { key: 'standard', label: 'Standard', color: 'var(--mid-color)' },
];
const ASSIGNABLE = ['business', 'pro', 'standard', 'enterprise', 'none'];

const TIMEFRAMES = [
  { key: 'last_month', label: 'Last month' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_3_months', label: 'Last 3 months' },
  { key: 'ytd', label: 'Year to date' },
];

const fmt = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const resolveTimeframe = (key: string): { str: string; label: string } => {
  const now = new Date();
  const today = fmt(now);
  switch (key) {
    case 'this_month':
      return {
        str: `${fmt(new Date(now.getFullYear(), now.getMonth(), 1))} to ${today}`,
        label: 'This month',
      };
    case 'last_3_months':
      return {
        str: `${fmt(new Date(now.getFullYear(), now.getMonth() - 2, 1))} to ${today}`,
        label: 'Last 3 months',
      };
    case 'ytd':
      return {
        str: `${fmt(new Date(now.getFullYear(), 0, 1))} to ${today}`,
        label: 'Year to date',
      };
    case 'last_month':
    default:
      return { str: 'last month', label: 'Last month' };
  }
};

const nfmt = (n: number) => n.toLocaleString();

// ov.databases is { entity, tariff, productImport, productExport }, each
// { count, items } — sum across all four categories for the combined KPI.
const sumDbBuckets = (
  databases: Record<string, { count: number; items: number }> | undefined,
  key: 'count' | 'items',
): number =>
  Object.values(databases || {}).reduce((sum, bucket) => sum + (Number(bucket?.[key]) || 0), 0);

interface Aggregates {
  users: any[];
  dbCount: number;
  dbItems: number;
  totalRuns: number;
  totalEntries: number;
  savedWork: { type: string; caseCount: number; timesRun: number }[];
  licenseCounts: Record<string, number>;
  assignedSeats: number;
  unassignedSeats: number;
  licenseByEmail: Record<string, string>;
  creditsUsed: number;
  creditsTotal: number;
}

const emptyAgg: Aggregates = {
  users: [],
  dbCount: 0,
  dbItems: 0,
  totalRuns: 0,
  totalEntries: 0,
  savedWork: [],
  licenseCounts: {},
  assignedSeats: 0,
  unassignedSeats: 0,
  licenseByEmail: {},
  creditsUsed: 0,
  creditsTotal: 0,
};

const unwrap = (resp: any): any[] =>
  resp?.success && Array.isArray(resp.result) ? resp.result : [];

//eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function LeadershipOverview() {
  const { cur_user } = useContext(GlobalContext);

  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([ALL_TEAMS]);
  const [selectedTeam, setSelectedTeam] = useState<TeamOption>(
    cur_user?.selected_team?.id
      ? { id: cur_user.selected_team.id, name: cur_user.selected_team.name }
      : ALL_TEAMS,
  );
  const [timeframeKey, setTimeframeKey] = useState('last_month');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [agg, setAgg] = useState<Aggregates>(emptyAgg);
  const [reloadKey, setReloadKey] = useState(0);

  // Allocation dialog
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [draftLicense, setDraftLicense] = useState('business');
  const [saving, setSaving] = useState(false);

  const timeframe = useMemo(() => resolveTimeframe(timeframeKey), [timeframeKey]);
  const isSingleTeam = selectedTeam.id !== ALL_TEAMS.id;

  // Load the team list once (ids + names) for the selector.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const resp = await GroupAPI.getGroupsForAdmin();
      if (cancelled || !resp?.success || !Array.isArray(resp.result)) return;
      const opts: TeamOption[] = resp.result
        .filter((g: any) => g?.id && g?.name)
        .map((g: any) => ({ id: g.id, name: g.name }))
        .sort((a: TeamOption, b: TeamOption) => a.name.localeCompare(b.name));
      setTeamOptions([ALL_TEAMS, ...opts]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load metrics whenever team / timeframe / manual reload changes.
  useEffect(() => {
    let cancelled = false;
    const cleanup = () => {
      cancelled = true;
    };
    const tf = timeframe.str;

    const teamsToQuery: TeamOption[] = isSingleTeam
      ? [selectedTeam]
      : teamOptions.filter((t) => t.id !== ALL_TEAMS.id);

    if (teamsToQuery.length === 0) {
      setAgg(emptyAgg);
      return cleanup;
    }

    (async () => {
      setLoading(true);
      try {
        // One aggregate call for users/databases/analyzers/saved-work.
        const overviewResp = await MetricsAPI.getOverview(teamsToQuery, tf);
        // Licenses + credits still come from their own services, per team.
        const licCred = await Promise.all(
          teamsToQuery.map(async (t) => {
            const [licRes, credRes] = await Promise.all([
              LicensesAPI.getLicensesForTeam(t.id),
              CreditsAPI.getCreditsSummary(t.id),
            ]);
            return {
              licenses: unwrap(licRes),
              credits: credRes?.success ? credRes.result : null,
            };
          }),
        );
        if (cancelled) return;

        const ov =
          overviewResp?.success && overviewResp.result
            ? (overviewResp.result as any)
            : null;
        if (!ov && overviewResp) {
          enqueueSnackbar(
            typeof overviewResp.result === 'string'
              ? overviewResp.result
              : 'Failed to load metrics',
            { variant: 'error' },
          );
        }

        // License + credit aggregation (client side, across queried teams)
        const licenseCounts: Record<string, number> = {};
        const licenseByEmail: Record<string, string> = {};
        let assignedSeats = 0;
        let unassignedSeats = 0;
        let creditsUsed = 0;
        let creditsTotal = 0;
        licCred.forEach((r) => {
          r.licenses.forEach((a: any) => {
            const tier = (a?.currentLicense || 'none').toLowerCase();
            licenseCounts[tier] = (licenseCounts[tier] || 0) + 1;
            if (tier === 'none') unassignedSeats += 1;
            else assignedSeats += 1;
            const email = (a?.email || '').toLowerCase();
            if (email) licenseByEmail[email] = tier;
          });
          if (r.credits) {
            creditsUsed += Number(r.credits.total_used_credits) || 0;
            creditsTotal += Number(r.credits.total_monthly_credits) || 0;
          }
        });

        setAgg({
          users: ov?.users ?? [],
          dbCount: sumDbBuckets(ov?.databases, 'count'),
          dbItems: sumDbBuckets(ov?.databases, 'items'),
          totalRuns: ov?.analyzers?.totalRuns ?? 0,
          totalEntries: ov?.analyzers?.totalEntries ?? 0,
          savedWork: ov?.savedWork ?? [],
          licenseCounts: licenseCounts ?? 0,
          assignedSeats: assignedSeats ?? 0,
          unassignedSeats: unassignedSeats ?? 0,
          licenseByEmail: licenseByEmail ?? 0,
          creditsUsed: creditsUsed ?? 0,
          creditsTotal: creditsTotal ?? 0,
        });
      } catch (err) {
        if (!cancelled) {
          enqueueSnackbar('Failed to load leadership metrics', {
            variant: 'error',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return cleanup;
  }, [selectedTeam, timeframeKey, teamOptions, timeframe.str, reloadKey, isSingleTeam]);

  // ---- Derived view data ----
  const totalTiered = agg.assignedSeats + agg.unassignedSeats || 1;
  const savedWorkTotalRuns = agg.savedWork.reduce((s, x) => s + x.timesRun, 0);
  const creditPct = agg.creditsTotal
    ? Math.round((agg.creditsUsed / agg.creditsTotal) * 100)
    : 0;

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agg.users;
    return agg.users.filter((u) =>
      `${u.user_first_name || ''} ${u.user_last_name || ''} ${u.username || ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [query, agg.users]);

  const bars = useMemo(() => buildActivityBars(agg.users), [agg.users]);

  // ---- Super-admin gate (after all hooks to preserve hook order) ----
  if (cur_user.bite_super_admin === false) {
    return <Redirect to='/' />;
  }

  const displayName = (u: any) =>
    `${u.user_first_name || ''} ${u.user_last_name || ''}`.trim() ||
    u.username ||
    'Unknown user';
  const initials = (u: any) => {
    const n = displayName(u);
    return n
      .split(' ')
      .map((p: string) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };
  const lastActive = (u: any) => {
    const d = new Date(u.last_activity_date);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  // ---- Allocation dialog ----
  const startEditing = (u: any) => {
    setEditingUser(u);
    setDraftLicense(
      agg.licenseByEmail[(u.username || '').toLowerCase()] || 'business',
    );
  };
  const saveAssignment = async () => {
    if (!editingUser || !isSingleTeam) return;
    setSaving(true);
    try {
      const resp = await LicensesAPI.assignLicenses(selectedTeam.id, {
        adminId: String(cur_user?.basics?.id || cur_user?.basics?.email || ''),
        changes: [{ memberId: String(editingUser.user_id), license: draftLicense }],
      });
      if (resp?.success) {
        enqueueSnackbar('License updated', { variant: 'success' });
        setEditingUser(null);
        setReloadKey((k) => k + 1); // refresh distribution + table
      } else {
        enqueueSnackbar(
          typeof resp?.result === 'string' ? resp.result : 'Update failed',
          { variant: 'error' },
        );
      }
    } catch (e) {
      enqueueSnackbar('Update failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const header = ['User', 'Email', 'License', 'Last Active', 'Team Scope', 'Timeframe'];
    const rows = filteredUsers.map((u) => [
      displayName(u),
      u.username || '',
      agg.licenseByEmail[(u.username || '').toLowerCase()] || '—',
      lastActive(u),
      selectedTeam.name,
      timeframe.label,
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    saveAs(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
      `BITE_${selectedTeam.name.replace(/\s+/g, '_')}_${timeframeKey}.csv`,
    );
  };

  return (
    <div className="lo-wrap">
      {/* Breadcrumb + header */}
      <div className="lo-breadcrumb">
        <HomeOutlinedIcon style={{ fontSize: 16, color: 'var(--action-color)' }} />
        <span>/</span>
        <span>Admin</span>
        <span>/</span>
        <span className="lo-crumb-active">Leadership Overview</span>
      </div>

      <div className="lo-header">
        <div>
          <h1 className="lo-title">Leadership Overview</h1>
          <p className="lo-subtitle">
            Organization-wide license allocation, tool usage, and user
            engagement.
          </p>
        </div>
        <div className="lo-controls">
          <Autocomplete
            disableClearable
            options={teamOptions}
            getOptionLabel={(o:any) => o.name}
            isOptionEqualToValue={(o:any, v:any) => o.id === v.id}
            value={selectedTeam}
            onChange={(_:any, v:any) => v && setSelectedTeam(v)}
            componentsProps={controlListComponentsProps}
            sx={{ width: 240, ...controlInputSx }}
            renderInput={(params:any) => (
              <TextField
                {...params}
                placeholder="Select team"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <PeopleAltOutlinedIcon
                        sx={{
                          color: 'var(--text-secondary)',
                          fontSize: 20,
                          marginRight: '8px',
                        }}
                      />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Select
            value={timeframeKey}
            onChange={(e:any) => setTimeframeKey(e.target.value)}
            MenuProps={controlMenuProps}
            renderValue={(val:any) => (
              <>
                <CalendarMonthOutlinedIcon
                  sx={{ color: 'var(--text-secondary)', fontSize: 20 }}
                />
                {TIMEFRAMES.find((t) => t.key === val)?.label ?? ''}
              </>
            )}
            sx={{ width: 180, ...controlSelectSx }}
          >
            {TIMEFRAMES.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={exportCsv}
            disabled={filteredUsers.length === 0}
          >
            Export
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <section className="lo-kpi-grid" aria-label="Key metrics">
        <KpiCard
          icon={<PeopleAltOutlinedIcon style={{ fontSize: 18 }} />}
          label="Unique active users"
          value={nfmt(agg.users.length)}
          note={`${selectedTeam.name} · ${timeframe.label}`}
          positive
        />
        <KpiCard
          icon={<KeyOutlinedIcon style={{ fontSize: 18 }} />}
          label="Assigned licenses"
          value={nfmt(agg.assignedSeats)}
          note={`${nfmt(agg.unassignedSeats)} unassigned`}
        />
        <KpiCard
          icon={<PaidOutlinedIcon style={{ fontSize: 18 }} />}
          label="Credits used"
          value={nfmt(agg.creditsUsed)}
          note={
            agg.creditsTotal
              ? `${creditPct}% of ${nfmt(agg.creditsTotal)} allowance`
              : 'No active allowance'
          }
        />
        <KpiCard
          icon={<AssessmentOutlinedIcon style={{ fontSize: 18 }} />}
          label="Analyzer runs"
          value={nfmt(agg.totalRuns)}
          note={`${nfmt(agg.totalEntries)} entries · ${nfmt(agg.dbItems)} DB items`}
        />
      </section>

      {/* Chart + license distribution */}
      <section className="lo-main-grid">
        <div className="lo-card">
          <div className="lo-section-head">
            <div>
              <h2 className="lo-h2">User activity distribution</h2>
              <p className="lo-sub">
                When active users were last seen in this window
              </p>
            </div>
            <span className="lo-sub">{timeframe.label}</span>
          </div>
          {bars.length === 0 ? (
            <div className="lo-empty">No user activity in this window.</div>
          ) : (
            <>
              <div className="lo-bars">
                {bars.map((b, i) => (
                  <div className="lo-bar-col" key={i} title={`${b.count} users`}>
                    <div
                      className="lo-bar"
                      style={{ height: `${b.pct}%` }}
                      aria-label={`${b.count} users`}
                    />
                  </div>
                ))}
              </div>
              <div className="lo-bar-labels">
                {bars.map((b, i) => (
                  <span key={i}>{b.label}</span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="lo-card">
          <h2 className="lo-h2">License distribution</h2>
          <p className="lo-sub">Current allocation by tier</p>
          <div style={{ marginTop: 20 }}>
            {TIER_META.map((tier) => {
              const count = agg.licenseCounts[tier.key] || 0;
              const pct = Math.round((count / totalTiered) * 100);
              return (
                <div className="lo-lic-row" key={tier.key}>
                  <div className="lo-lic-top">
                    <span>{tier.label}</span>
                    <span>
                      <strong className="lo-num">{nfmt(count)}</strong>
                      <span style={{ marginLeft: 8, color: 'var(--text-tertiary)' }}>
                        {pct}%
                      </span>
                    </span>
                  </div>
                  <div className="lo-lic-track">
                    <div
                      className="lo-lic-fill"
                      style={{ width: `${pct}%`, background: tier.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="lo-lic-total">
            <span>Unassigned licenses</span>
            <strong className="lo-num" style={{ color: 'var(--text-primary-color)' }}>
              {nfmt(agg.unassignedSeats)}
            </strong>
          </div>
        </div>
      </section>

      {/* Saved work by type */}
      <section className="lo-card" style={{ marginTop: 16, padding: 0 }}>
        <div className="lo-panel-head">
          <div>
            <h2 className="lo-h2">Saved work by type</h2>
            <p className="lo-sub">Cases and how many times they have been run</p>
          </div>
          <span className="lo-sub">{nfmt(savedWorkTotalRuns)} total runs</span>
        </div>
        <div className="lo-table-wrap">
          <table className="lo-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Cases</th>
                <th>Times run</th>
              </tr>
            </thead>
            <tbody>
              {agg.savedWork.length === 0 ? (
                <tr>
                  <td colSpan={3} className="lo-empty">
                    No saved work in this window.
                  </td>
                </tr>
              ) : (
                agg.savedWork.map((s) => (
                  <tr key={s.type}>
                    <td>
                      <span className="lo-badge">{s.type}</span>
                    </td>
                    <td className="lo-num">{nfmt(s.caseCount)}</td>
                    <td className="lo-num">{nfmt(s.timesRun)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* User activity table */}
      <section className="lo-card" style={{ marginTop: 16, padding: 0 }}>
        <div className="lo-panel-head">
          <div>
            <h2 className="lo-h2">User activity</h2>
            <p className="lo-sub">
              {selectedTeam.name} · {timeframe.label}
            </p>
          </div>
          <div style={{ position: 'relative', width: 280, maxWidth: '100%' }}>
            <SearchOutlinedIcon
              style={{
                position: 'absolute',
                left: 10,
                top: 8,
                fontSize: 18,
                color: 'var(--text-tertiary)',
              }}
            />
            <TextField
              size="small"
              fullWidth
              value={query}
              onChange={(e:any) => setQuery(e.target.value)}
              placeholder="Search users"
              inputProps={{ style: { paddingLeft: 26 } }}
            />
          </div>
        </div>
        <div className="lo-table-wrap">
          <table className="lo-table">
            <thead>
              <tr>
                <th>User</th>
                <th>License</th>
                <th>Last active</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>
                    <div className="lo-empty">
                      <span className="lo-loading-row">
                        <CircularProgress size={16} /> Loading metrics…
                      </span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="lo-empty">
                    No active users for this team and timeframe.
                  </td>
                </tr>
              ) : (
                filteredUsers.slice(0, 25).map((u) => {
                  const tier =
                    agg.licenseByEmail[(u.username || '').toLowerCase()];
                  return (
                    <tr key={u.user_id}>
                      <td>
                        <div className="lo-user-cell">
                          <span className="lo-avatar">{initials(u)}</span>
                          <span>
                            <span className="lo-user-name">{displayName(u)}</span>
                            <br />
                            <span className="lo-user-email">{u.username}</span>
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="lo-badge">{tier || '—'}</span>
                      </td>
                      <td style={{ color: 'var(--text-tertiary)' }}>
                        {lastActive(u)}
                      </td>
                      <td>
                        <span className="lo-status-active">● Active</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<TuneOutlinedIcon />}
                          disabled={!isSingleTeam}
                          title={
                            isSingleTeam
                              ? 'Manage allocation'
                              : 'Select a single team to manage allocations'
                          }
                          onClick={() => startEditing(u)}
                        >
                          Manage
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="lo-table-foot">
          <span>
            Showing {Math.min(25, filteredUsers.length)} of{' '}
            {nfmt(filteredUsers.length)} users
          </span>
        </div>
      </section>

      {/* Allocation dialog */}
      <Dialog
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Manage allocation</DialogTitle>
        <DialogContent>
          <p className="lo-sub" style={{ marginTop: 0 }}>
            Assign a license for {editingUser ? displayName(editingUser) : ''} on{' '}
            {selectedTeam.name}.
          </p>
          <label
            className="lo-kpi-label"
            style={{ display: 'block', margin: '12px 0 6px' }}
          >
            License
          </label>
          <Select
            size="small"
            fullWidth
            value={draftLicense}
            onChange={(e:any) => setDraftLicense(e.target.value)}
          >
            {ASSIGNABLE.map((l) => (
              <MenuItem key={l} value={l} style={{ textTransform: 'capitalize' }}>
                {l === 'none' ? 'Unassigned' : l}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingUser(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveAssignment} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Buckets user last_activity_date into up to 12 bars across [min, max].
function buildActivityBars(
  users: any[],
): { label: string; count: number; pct: number }[] {
  const dates = users
    .map((u) => new Date(u.last_activity_date).getTime())
    .filter((t) => !isNaN(t));
  if (dates.length === 0) return [];
  const min = Math.min(...dates);
  const max = Math.max(...dates);
  const BUCKETS = 12;
  const span = Math.max(max - min, 1);
  const size = span / BUCKETS;
  const counts = new Array(BUCKETS).fill(0);
  dates.forEach((t) => {
    const idx = Math.min(BUCKETS - 1, Math.floor((t - min) / size));
    counts[idx] += 1;
  });
  const peak = Math.max(...counts, 1);
  return counts.map((c, i) => {
    const bucketStart = new Date(min + i * size);
    return {
      label: `${bucketStart.getMonth() + 1}/${bucketStart.getDate()}`,
      count: c,
      pct: Math.max(2, Math.round((c / peak) * 100)),
    };
  });
}

function KpiCard({
  icon,
  label,
  value,
  note,
  positive,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  note: string;
  positive?: boolean;
}) {
  return (
    <div className="lo-card">
      <div className="lo-kpi-top">
        <span className="lo-kpi-label">{label}</span>
        <span className="lo-kpi-icon">{icon}</span>
      </div>
      <p className="lo-kpi-value">{value}</p>
      <p className={`lo-kpi-note ${positive ? 'lo-positive' : ''}`}>{note}</p>
    </div>
  );
}

export default LeadershipOverview;
